import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Search, Loader2, ExternalLink, FileText } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

const TCCSearchPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("appelant") || "");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    const appelant = searchParams.get("appelant");
    if (appelant) {
      setQuery(appelant);
      performSearch(appelant);
    }
  }, [searchParams]);

  const performSearch = async (searchTerm) => {
    if (!searchTerm?.trim()) {
      toast.error("Veuillez entrer un nom d'appelant");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/tcc_appelant_dossiers`, {
        params: {
          appelant: searchTerm,
          lang: "f",
          pretty: false
        }
      });
      setResults(response.data);
      
      if (response.data.nombre_de_dossiers_trouves === 0) {
        toast.info("Aucun dossier trouvé pour cet appelant");
      } else {
        toast.success(`${response.data.nombre_de_dossiers_trouves} dossier(s) trouvé(s)`);
      }
    } catch (error) {
      console.error("TCC search error:", error);
      toast.error(error.response?.data?.erreur || "Erreur lors de la recherche TCC");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/tcc?appelant=${encodeURIComponent(query)}`);
  };

  const openOfficialSite = () => {
    window.open("https://www.tcc-cci.ca/fr/pages/trouver-un-dossier-de-la-Cour", "_blank");
  };

  const handleBack = () => {
    // Si la page a été ouverte dans un nouvel onglet (pas d'historique), fermer l'onglet
    if (window.history.length <= 2) {
      window.close();
      // Si window.close() ne fonctionne pas (restrictions navigateur), naviguer vers /search
      navigate("/search");
    } else {
      // Sinon, revenir à la page précédente dans l'historique
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              onClick={handleBack}
              variant="outline"
              className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-5xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                🏛️ Cour Canadienne de l'Impôt
              </h1>
              <p className="text-lg text-gray-400">Recherche de dossiers d'appelants</p>
            </div>
          </div>
          <Button
            onClick={openOfficialSite}
            variant="outline"
            className="bg-transparent border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Site Officiel TCC
          </Button>
        </div>

        {/* Search Bar */}
        <div className="glass rounded-2xl p-6 mb-6">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              data-testid="tcc-search-input"
              type="text"
              placeholder="Nom de famille ou entreprise de l'appelant..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 h-14 text-lg"
            />
            <Button
              data-testid="tcc-search-btn"
              type="submit"
              disabled={loading}
              className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Rechercher
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Results */}
        {results && (
          <div className="glass rounded-2xl p-6 fade-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">
                  Résultats pour "{results.appelant_recherche}"
                </h2>
                <p className="text-gray-400 mt-1">
                  {results.nombre_de_dossiers_trouves} dossier(s) trouvé(s)
                </p>
              </div>
            </div>

            {results.dossiers && results.dossiers.length > 0 ? (
              <div className="space-y-4">
                {results.dossiers.map((dossier, index) => (
                  <div
                    key={index}
                    data-testid={`dossier-${index}`}
                    className="p-5 rounded-lg bg-gray-900/30 border border-gray-800 hover:border-blue-500/50 transition-colors card-hover"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Column 1 */}
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-400">Numéro de dossier</span>
                          <p className="text-lg font-semibold text-blue-400">
                            {dossier.numero_dossier || "N/A"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-400">Nom complet</span>
                          <p className="text-lg font-medium">{dossier.nom_complet || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-400">Date de dépôt</span>
                          <p className="text-base">{dossier.date_depot || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-400">Case ID</span>
                          <p className="text-base font-mono">{dossier.case_id || "N/A"}</p>
                        </div>
                      </div>

                      {/* Column 2 */}
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-400">Statut du dossier</span>
                          <p className="text-base">
                            <span className={`px-3 py-1 rounded-full text-sm ${
                              dossier.statut_dossier?.toLowerCase().includes('active') 
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : dossier.statut_dossier?.toLowerCase().includes('closed')
                                ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                                : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                            }`}>
                              {dossier.statut_dossier || "N/A"}
                            </span>
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-400">Disposition</span>
                          <p className="text-base">{dossier.disposition || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-400">Loi concernée</span>
                          <p className="text-base">{dossier.loi_concernee || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-400">Lieu d'audience</span>
                          <p className="text-base">{dossier.lieu_audience || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <p className="text-xl text-gray-400">Aucun dossier trouvé</p>
                <p className="text-sm text-gray-500 mt-2">
                  Essayez avec un autre nom d'appelant
                </p>
              </div>
            )}
          </div>
        )}

        {/* Info Box */}
        {!results && !loading && (
          <div className="glass rounded-2xl p-6 text-center">
            <p className="text-gray-400 text-lg">
              Entrez le nom de famille ou le nom d'entreprise d'un appelant pour rechercher ses dossiers à la Cour canadienne de l'impôt.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TCCSearchPage;
